import { i18n } from '@lingui/core'
import { t } from '@lingui/macro'
import { useMemo } from 'react'
import { Outlet, useNavigate, useParams } from 'react-router-dom'

import { globalDefinitions } from '@/defined-translations'
import { useHideNativeNavigation } from '@/layout/providers/LayoutProvider'
import { ErrorLayout } from '@/modules/shared/components/error-layout/ErrorLayout'
import { AnimatedLoader } from '@/modules/shared/components/loader/Loader'
import { NotFoundError } from '@/modules/shared/components/not-found-error/NotFoundError'
import { isProduction } from '@/utils/environment'
import { AwellHostedPage } from '../../components/awell-hosted-page/AwellHostedPage'
import { useQuestionnaireCareplanActivity } from '../../hooks/useQuestionnaireCareplanActivity'
import { QuestionnaireEngineProvider } from '../../providers/QuestionnaireEngineProvider'

const QuestionnaireRootPage = () => {
  useHideNativeNavigation()

  const navigate = useNavigate()
  const { activityId } = useParams()

  const {
    data: careplanActivity,
    isLoading: isCareplanActivityLoading,
    error: careplanActivityError,
  } = useQuestionnaireCareplanActivity(activityId)

  // Template variables
  // ------------------------------------------------------------------------- /

  const isAwell = useMemo(() => {
    return careplanActivity?.questionnaire?.driver === 'awell'
  }, [careplanActivity])

  const errorMessage = useMemo(() => {
    const defaultMessage = t({
      id: 'questionnaireActivity.errorMessage',
      message: 'Er is iets misgegaan bij het laden van de vraaglijst',
    })
    if (careplanActivityError && !isProduction()) {
      return defaultMessage + ': ' + careplanActivityError.message
    }

    return defaultMessage
  }, [careplanActivityError])

  // Interaction methods
  // ------------------------------------------------------------------------- /
  function handleClick() {
    navigate('/home')
  }

  if (isAwell && activityId) {
    return <AwellHostedPage activityId={Number(activityId)} />
  }

  if (isCareplanActivityLoading) {
    return <AnimatedLoader />
  }

  if (!activityId || !careplanActivity) {
    return <NotFoundError onClick={handleClick} />
  }

  if (careplanActivityError || !careplanActivity.questionnaire) {
    return (
      <ErrorLayout
        title={t({
          id: 'questionnaireActivity.errorTitle',
          message: 'Er is iets misgegaan bij het laden van de vraaglijst',
        })}
        message={errorMessage}
        cta={{
          label: i18n._(globalDefinitions.goBack),
          onClick: handleClick,
        }}
      />
    )
  }

  return (
    <QuestionnaireEngineProvider activity={careplanActivity}>
      <Outlet />
    </QuestionnaireEngineProvider>
  )
}

export { QuestionnaireRootPage }
